// Canonical encounter websocket adoption for FT3D and non-map pages.
// The retired 2-D map reconciler used to animate a second DOM map here. FT3D owns
// presentation now; this module retains only cache, subscription and shared UI work.

import {
    encounterField,
    encounterCreatureField,
    setEncounterField,
    type EncounterArr,
    type StatusEffectInstance,
} from '../shared/array-types';
import { lightTypeName, weatherTypeName } from '../shared/env-types';
import { ftCarryPermissionsForward, ftCarryInventoryPermissionsForward } from './caster-cache';
import { recoverActiveViewerCasterAfterEncounterDeparture } from './caster-update';
import { bankLegacyRowTruth, pendingFor } from './movement-truth';
import type { MovementTruthRow } from '../janus-engine/movement/client-truth-commit';
import { ftUpdateActivityTokenBars } from './activity-token-bars';
import type { WSMessage } from './types';
import { traceEncounterTruth } from './encounter-truth-trace';
import { markStall } from '../shared/stall-mark';

type EncounterUpdateFrame = Extract<WSMessage, { type: 'encounter_update' }>;

type LooseRecord = Record<string, unknown>;
const looseRecord = (value: unknown): LooseRecord | null =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? value as LooseRecord : null;

/**
 * A creature row that JOINS the roster (a GM move-in, a spawn) is template-thinned: its compiled carriers and its statuses
 * name definitions that live once in the encounter's `catalog`. The wire that brings it does not always bring those
 * definitions — a delta ships no catalog, and the full `encounter_update` push after a GM move-in carried the encounter's
 * previous catalog (140 entries where get_encounter_arr.php returns 297; owner report 2026-09-30). A row naming a definition
 * this page holds nowhere (catalog or object cache) cannot be served: the client-truth lanes refuse it ("Force Move
 * refused: one of its statuses or abilities is not loaded") and the FT3D scene draws an initials standee.
 */
export function unheldDefinitionIds(row: unknown, catalog: unknown): string[] {
    const held = looseRecord(catalog) ?? {};
    const objectCache = window.FateTide?.objectCache;
    const known = (value: unknown): boolean => {
        const id = String(Array.isArray(value) ? value[0] ?? '' : value ?? '').trim();
        if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(id)) return true;
        return held[id] !== undefined || held[id.toUpperCase()] !== undefined || held[id.toLowerCase()] !== undefined
            || !!objectCache?.get?.(id) || !!objectCache?.get?.(id.toUpperCase()) || !!objectCache?.get?.(id.toLowerCase());
    };
    const unheld = new Set<string>();
    const source = looseRecord(row);
    const abilities = looseRecord(looseRecord(looseRecord(source?.computed)?.creature)?.abilities);
    for (const slot of Object.values(abilities ?? {})) {
        for (const entry of Array.isArray(slot) ? slot : []) {
            const item = looseRecord(entry);
            // An entry that carries its own definition inline needs no catalog row.
            if (!item || looseRecord(looseRecord(item.item_arr)?.item_layer_types) || looseRecord(item.item_layer_types)) continue;
            // A bare inline definition names itself under items.item_id (janus-engine/movement/carrier-entry.ts).
            const itemId = item.item_id ?? looseRecord(item.items)?.item_id;
            if (!known(itemId)) unheld.add(String(itemId).toUpperCase());
        }
    }
    const statuses = looseRecord(source?.statuses);
    for (const mode of ['target', 'caster']) {
        const list = statuses?.[mode];
        for (const entry of Array.isArray(list) ? list : list ? [list] : []) {
            const status = looseRecord(entry);
            if (status && !looseRecord(looseRecord(status.status_arr)?.item_layer_types) && !known(status.status_item_id)) unheld.add(String(status.status_item_id).toUpperCase());
        }
    }
    return [...unheld];
}

/** Definition ids a heal was already spent on, per encounter: an id the full read does not carry either (a definition that is
 *  not catalogued at all) must not buy a full read on every later join that names it. */
const catalogHealAttempted = new Map<string, Set<string>>();
/** True when `row` names a definition this page lacks that no heal has been spent on yet — and records those ids as spent. */
export function claimCatalogHeal(encounterId: string, row: unknown, catalog: unknown): boolean {
    const ids = unheldDefinitionIds(row, catalog);
    const attempted = catalogHealAttempted.get(encounterId) ?? new Set<string>();
    catalogHealAttempted.set(encounterId, attempted);
    const fresh = ids.filter(id => !attempted.has(id));
    fresh.forEach(id => attempted.add(id));
    return fresh.length > 0;
}

/** The full-read heal (encounter-delta.ts owns it: one in-flight get_encounter_arr.php per encounter). */
let encounterCatalogHeal: ((encounterId: string) => void) | null = null;
export function registerEncounterCatalogHeal(heal: (encounterId: string) => void): void { encounterCatalogHeal = heal; }

function statusRowsOf(carrier: unknown): StatusEffectInstance[] {
    const rows: unknown = ftCreatureStatuses(carrier);
    return Array.isArray(rows)
        ? rows.filter((row): row is StatusEffectInstance => row !== null && typeof row === 'object' && !Array.isArray(row))
        : [];
}

function encounterSpatialBoundsChanged(
    oldArr: EncounterArr | null | undefined,
    newArr: EncounterArr | null | undefined,
): boolean {
    if (!newArr) return false;
    if (!oldArr) return true;
    const keys = [
        'encounter_ceiling',
        'encounter_max_x',
        'encounter_max_y',
        'encounter_min_z',
        'encounter_max_z',
    ];
    const norm = (arr: EncounterArr, key: string): string => {
        const value = encounterField(arr, key);
        return value == null ? '' : String(value);
    };
    return keys.some((key) => norm(oldArr, key) !== norm(newArr, key));
}

function ftRealiasCastersFromEncounterBlob(encounterArr: EncounterArr, privacyOnly = false): void {
    // This path also installs initial HTTP snapshots, after the pre-template module scan.
    // Notify definition readers even when no caster alias exists yet.
    if (!privacyOnly) window.dispatchEvent(new CustomEvent('ft:encounter-installed', { detail: { encounterArr } }));
    const casterCache = window.FateTide?.casterCache;
    if (!(casterCache instanceof Map)) return;
    const rows = Array.isArray(encounterArr?.encounter_creatures_arr)
        ? encounterArr.encounter_creatures_arr
        : [];

    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const id = encounterCreatureField<string>(row.encounter_creatures, 'encounter_creature_id');
        if (!id || !casterCache.has(id)) continue;
        const cached = casterCache.get(id);
        if (privacyOnly) {
            const nextPrivate = Number(encounterCreatureField(
                row.encounter_creatures,
                'encounter_creature_private',
            ) ?? 0) === 1;
            const priorPrivate = Number(encounterCreatureField(
                cached?.encounter_creatures,
                'encounter_creature_private',
            ) ?? 0) === 1;
            if (nextPrivate === priorPrivate) continue;
            if (cached?.encounter_creatures && typeof cached.encounter_creatures === 'object') {
                cached.encounter_creatures.encounter_creature_private = nextPrivate ? 1 : 0;
            }
            continue;
        }
        if (cached === row) continue;
        ftCarryPermissionsForward(row, cached?.permissions);
        if (cached) ftCarryInventoryPermissionsForward(row, cached);
        if (cached && typeof cached._ws_version === 'number' && typeof row._ws_version !== 'number') {
            row._ws_version = cached._ws_version;
        }
        casterCache.set(id, row);
        try { window.ftRerenderOpenInfoPanel?.(id); } catch { /* non-fatal */ }
        try { window.ftRerenderPcSheet?.(id); } catch { /* non-fatal */ }
        try { window.ftRerenderClassMechanicBars?.(id); } catch { /* non-fatal */ }
    }
}

function ftPatchEncounterCreaturePrivacy(
    retained: EncounterArr | null | undefined,
    delta: EncounterArr,
): boolean {
    const deltaRow = Array.isArray(delta.encounter_creatures_arr) ? delta.encounter_creatures_arr[0] : null;
    const id = encounterCreatureField<string>(deltaRow?.encounter_creatures, 'encounter_creature_id');
    if (!retained || !id || !Array.isArray(retained.encounter_creatures_arr)) return false;
    const target = retained.encounter_creatures_arr.find((row) => (
        encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') === id
    ));
    if (!target?.encounter_creatures || typeof target.encounter_creatures !== 'object') return false;
    target.encounter_creatures.encounter_creature_private = Number(encounterCreatureField(
        deltaRow?.encounter_creatures,
        'encounter_creature_private',
    ) ?? 0) === 1 ? 1 : 0;
    return true;
}

function dispatchEncounterState(encounterId: string, encounterArr: EncounterArr, subType: string | null, inventoryUnchanged = false, inventoryRows?: unknown[]): void {
    window.dispatchEvent(new CustomEvent('ft:encounter-state-received', {
        detail: { encounterId, encounterArr, subType, ...(inventoryRows ? { inventoryRows } : {}), ...(inventoryUnchanged ? { inventoryUnchanged } : {}) },
    }));
}

window.addEventListener('ft:inventory-base-compiled', event => {
    const detail = looseRecord((event as CustomEvent).detail);
    const id = String(detail?.entityId ?? '').toUpperCase();
    const encounter = window.FateTide?.encounterCache?.get(id);
    if (encounter && Array.isArray(encounter.encounter_inventories_arr)) {
        dispatchEncounterState(id, encounter, 'inventory_base_compiled', detail?.inventoryUnchanged === true,
            Array.isArray(detail?.inventoryRows) ? detail.inventoryRows : undefined);
    }
});

function encounterContainsCaster(encounterArr: EncounterArr | null | undefined, casterId: string): boolean {
    if (!encounterArr || !casterId || !Array.isArray(encounterArr.encounter_creatures_arr)) return false;
    const expected = casterId.toUpperCase();
    return encounterArr.encounter_creatures_arr.some((row) => (
        String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase()
            === expected
    ));
}

function viewerPlayerCasterId(encounterArr: EncounterArr | null | undefined, preferredId = ''): string {
    if (!encounterArr || !Array.isArray(encounterArr.encounter_creatures_arr)) return '';
    const preferred = preferredId.toUpperCase();
    if (preferred && encounterContainsCaster(encounterArr, preferred)) return preferredId;
    const owned = encounterArr.encounter_creatures_arr.find((row) => {
        const permissions = row?.permissions as { player?: unknown } | undefined;
        const mayControl = permissions?.player === true || Number(permissions?.player ?? 0) === 1;
        return mayControl && row?.player_characters != null;
    });
    return String(encounterCreatureField(
        owned?.encounter_creatures,
        'encounter_creature_id',
    ) ?? '').trim();
}

function encounterControlStateChanged(
    previous: EncounterArr | null | undefined,
    next: EncounterArr,
): boolean {
    if (!previous) return false;
    const normalized = (arr: EncounterArr, key: string): string => {
        const fallback = key === 'in_combat' || key === 'current_round' ? 0 : '';
        return String(encounterField(arr, key) ?? fallback);
    };
    return ['in_combat', 'current_round', 'turn_phase'].some((key) => (
        normalized(previous, key) !== normalized(next, key)
    ));
}

/**
 * A compact encounter_stats_update is published at mutation time, while a full encounter rebuild
 * can finish later with a snapshot captured before that mutation. Preserve the ordered compact
 * assertion during that bounded overlap so a stale canonical frame cannot visibly undo a GM light,
 * weather, or combat-control change.
 */
function reconcileEncounterMutationAuthority(encounterId: string, next: EncounterArr): void {
    const authority = window.FateTide?.encounterScalarAuthority;
    const entry = authority?.[encounterId];
    if (!entry) return;

    const AUTHORITY_TTL_MS = 15_000;
    if ((Date.now() - entry.ts) >= AUTHORITY_TTL_MS) {
        if (authority) delete authority[encounterId];
        return;
    }

    const scalarKeys = [
        'in_combat',
        'current_round',
        'current_encounter_creature_id_turn',
        'turn_phase',
        'pending_main_turn_encounter_creature_id',
    ];
    for (const key of scalarKeys) {
        if (!Object.prototype.hasOwnProperty.call(entry.values, key)) continue;
        const authoritativeValue = entry.values[key];
        if (String(encounterField(next, key) ?? '') === String(authoritativeValue ?? '')) continue;
        const bucket = next.encounters;
        if (bucket && typeof bucket === 'object' && key in bucket) bucket[key] = authoritativeValue;
        else next[key] = authoritativeValue;
        ftDebugLog('ft3d encounter', {
            mutationAuthorityOverride: key,
            encounterId,
        });
    }

    for (const key of ['encounter_light', 'encounter_weather']) {
        if (!Object.prototype.hasOwnProperty.call(entry.values, key)) continue;
        const authoritativeValue = entry.values[key] as string | null | undefined;
        if (String(encounterField(next, key) ?? '') === String(authoritativeValue ?? '')) continue;
        const itemName = (key === 'encounter_light'
            ? lightTypeName(authoritativeValue)
            : weatherTypeName(authoritativeValue)) || undefined;
        setEncounterField(next, key, authoritativeValue, itemName);
        ftDebugLog('ft3d encounter', {
            mutationAuthorityOverride: key,
            encounterId,
        });
    }
}

async function handleEncounterUpdate(parsedData: EncounterUpdateFrame, correction: boolean | ReadonlySet<string> = false): Promise<void> {
    markStall('ws:full-push');
    const next = parsedData.encounter_arr;
    traceEncounterTruth('encounter-update:received', String(encounterField(next, 'encounter_id') ?? ''), { version: parsedData.version, subType: parsedData.sub_type }, next);
    await window.ftAdoptInventoryBase?.(next);
    if (typeof next._entity_refusal === 'string') {
        window.ftDiscardInventoryBase?.(next);
        return;
    }
    const encounterId = encounterField<string>(next, 'encounter_id');
    if (!encounterId) {
        ftDebugLog('ft3d encounter', { warn: 'encounter_update missing encounter id' });
        return;
    }
    const subType = typeof parsedData.sub_type === 'string' ? parsedData.sub_type : null;
    const cache = window.FateTide?.encounterCache;
    const held = cache instanceof Map ? cache.get(encounterId) as EncounterArr | undefined : undefined;
    const isActiveEncounter = encounterId === String(window.ftGetActiveEncounterId?.() || '').trim();
    const activeViewerCasterId = String(window.FateTide?.activeViewerCasterId || '').trim();
    const viewerCasterId = viewerPlayerCasterId(held, activeViewerCasterId);
    const activeViewerDeparted = !window.FateTide?.user?.isGM
        && isActiveEncounter
        && !!viewerCasterId
        && !encounterContainsCaster(next, viewerCasterId);
    const rebuildGmControls = isActiveEncounter && encounterControlStateChanged(held, next);
    const heldVersion = typeof held?._ws_version === 'number' ? held._ws_version : undefined;
    if (!correction && typeof parsedData.version === 'number' && heldVersion !== undefined && parsedData.version < heldVersion) {
        ftDebugLog('ft3d encounter', { staleFullSnapshotIgnored: encounterId, held: heldVersion, incoming: parsedData.version });
        return;
    }

    if (subType === 'encounter_creature_private_toggle') {
        ftPatchEncounterCreaturePrivacy(held, next);
        ftPatchEncounterCreaturePrivacy(window.FateTide?.encounterArr as EncounterArr | undefined, next);
        ftRealiasCastersFromEncounterBlob(next, true);
        dispatchEncounterState(encounterId, next, subType);
        return;
    }

    if (
        typeof window.refreshMapMarkerRostersForEncounters === 'function'
        && document.querySelector(`.leaflet-marker-icon[data-encounter_id="${encounterId}"]`)
    ) {
        try { window.refreshMapMarkerRostersForEncounters([encounterId]); } catch { /* helper logs */ }
    }

    reconcileEncounterMutationAuthority(encounterId, next);
    // Keep pending renders and complete newer confirmations, including answers that landed during adoption.
    const heldMembers = new Map((held?.encounter_creatures_arr ?? []).map(row =>
        [String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase(), row]));
    const members = Array.isArray(next.encounter_creatures_arr) ? next.encounter_creatures_arr : [];
    for (let index = 0; index < members.length; index++) {
        const id = String(encounterCreatureField(members[index]?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
        const canonical = heldMembers.get(id);
        const refused = correction instanceof Set ? correction.has(id) : correction;
        if (!refused && canonical && (pendingFor(encounterId, id) > 0
            || Number(encounterCreatureField(canonical.encounter_creatures, 'movement_rev') ?? 0)
                > Number(encounterCreatureField(members[index]?.encounter_creatures, 'movement_rev') ?? 0))) members[index] = canonical;
    }
    if (held) ftCarryInventoryPermissionsForward(next, held);
    // Definitions are keyed by item id and only ever added to a live encounter: a full push whose catalog lacks entries the
    // page already holds (the stale-catalog push after a GM move-in, owner report 2026-09-30) keeps them; the push's own win.
    const heldCatalog = looseRecord(held?.catalog);
    if (heldCatalog) {
        const nextRoot = next as unknown as LooseRecord;
        const nextCatalog = looseRecord(nextRoot.catalog) ?? {};
        for (const [id, definition] of Object.entries(heldCatalog)) if (!(id in nextCatalog)) nextCatalog[id] = definition;
        nextRoot.catalog = nextCatalog;
    }
    if (typeof parsedData.version === 'number') {
        next._ws_version = parsedData.version;
    } else if (typeof next._ws_version !== 'number' && heldVersion !== undefined) {
        next._ws_version = heldVersion;
    }

    const finishTrace = traceEncounterTruth('encounter-update', encounterId, { version: parsedData.version, subType }, next);
    ftRealiasCastersFromEncounterBlob(next);
    if (cache instanceof Map) cache.set(encounterId, next);
    finishTrace?.();
    if (window.FateTide && isActiveEncounter) {
        window.FateTide.encounterArr = next;
    }
    // Enclosing paints may preserve a pending optimistic member. Its outbox answer owns confirmation.
    for (const row of Array.isArray(next.encounter_creatures_arr) ? next.encounter_creatures_arr : []) {
        const id = encounterCreatureField<string>(row?.encounter_creatures, 'encounter_creature_id');
        if (id && row && pendingFor(encounterId, id) === 0) bankLegacyRowTruth(encounterId, id, row as unknown as MovementTruthRow, 'encounter-state');
    }
    dispatchEncounterState(encounterId, next, subType);
    // A creature that JOINED with this push but whose definitions the push's catalog lacks: heal with the full read, which
    // carries them. Only rows new against the held store count, so the healed install (same roster) cannot re-trigger it.
    if (held && encounterCatalogHeal) {
        const heldIds = new Set((Array.isArray(held.encounter_creatures_arr) ? held.encounter_creatures_arr : [])
            .map(row => String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase()));
        const joinedUnheld = (Array.isArray(next.encounter_creatures_arr) ? next.encounter_creatures_arr : []).some(row => {
            const id = String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase();
            return id !== '' && !heldIds.has(id) && claimCatalogHeal(encounterId, row, next.catalog);
        });
        if (joinedUnheld) {
            ftDebugLog('ft3d encounter', { joinedRowCatalogHeal: encounterId });
            encounterCatalogHeal(encounterId);
        }
    }

    if (isActiveEncounter) {
        try { syncMyCampaignTimeControlsToEncounterInCombat(next); } catch { /* page does not mount it */ }
    }
    if (rebuildGmControls) {
        // The compact encounter_stats_update normally owns this repaint. A viewer can miss that
        // frame (or subscribe after it) and still receive the canonical encounter snapshot; keep
        // the GM combat controls consistent with the newly adopted active encounter in that path.
        try { await buildTurnWidgetGMControls(); } catch { /* page does not mount GM controls */ }
    }
    // The summary card is the ACTIVE encounter's: getEncounterSettingSummary() stores a passed blob under the page's own
    // sidelog encounter id, so a NON-active install (a sibling encounter fetched by the actor-row loader or a move heal) wrote
    // that encounter's blob over the active one's cache key (N6 2026-10-01, BUG LEDGER).
    const summaryEncounterId = (() => {
        try { return String((window as unknown as { getSidelogContext?: () => { encounter_id?: unknown } | null }).getSidelogContext?.()?.encounter_id ?? '').trim().toUpperCase(); }
        catch { return ''; }
    })();
    if (summaryEncounterId === String(encounterId).toUpperCase()) {
        try { getEncounterSettingSummary(next, null); } catch { /* page does not mount it */ }
    }
    try {
        if (
            typeof window.ftRerenderOpenSideInfo === 'function'
        ) {
            const ids = typeof window.ftCollectInventoryIds === 'function'
                ? window.ftCollectInventoryIds(next)
                : [];
            window.ftRerenderOpenSideInfo(ids);
        }
    } catch { /* non-fatal */ }
    ftUpdateActivityTokenBars();
    if (activeViewerDeparted) {
        await recoverActiveViewerCasterAfterEncounterDeparture(encounterId, viewerCasterId);
    }
    ftDebugLog('ft3d encounter', { adopted: encounterId, subType });
}

/**
 * The ONE-ROW echo (2026-09-10, owner: "a move must patch one row, not re-intern the encounter").
 * A creature-only `encounter_update_delta` — section patches / whole-row adopts on creatures the store
 * already holds, no encounter-shell change, no inventory adopt or prune, membership unchanged — has
 * been applied IN PLACE by encounter-delta.ts. Every old-vs-new read the full handler performs
 * (bounds, control state, mutation authority, departure, encounter summary) answers "unchanged" by
 * construction, and its whole-roster passes (re-alias, re-intern every attack definition, re-bank
 * every row, walk the blob for inventory ids) cost 1.5 s of main thread in a 1,141-row scene for two
 * patched rows. This path does only what the patched rows need and tags the state event so the FT3D
 * parent can project ACTORS ONLY into the scene (`subType: 'creature_rows_patched'`,
 * `inventoryUnchanged: true`). Rows were patched in place, so casterCache aliases already point at
 * the patched objects; the interning of the patched rows rides `ft:encounter-rows-patched`.
 */
function handleEncounterRowsPatched(encounterArr: EncounterArr, creatureIds: readonly string[]): void {
    const encounterId = encounterField<string>(encounterArr, 'encounter_id');
    if (!encounterId) return;
    if (
        typeof window.refreshMapMarkerRostersForEncounters === 'function'
        && document.querySelector(`.leaflet-marker-icon[data-encounter_id="${encounterId}"]`)
    ) {
        try { window.refreshMapMarkerRostersForEncounters([encounterId]); } catch { /* helper logs */ }
    }
    const rows = Array.isArray(encounterArr.encounter_creatures_arr) ? encounterArr.encounter_creatures_arr : [];
    const wanted = new Set(creatureIds.map((id) => id.toUpperCase()));
    const patchedRows = rows.filter((row) => wanted.has(
        String(encounterCreatureField(row?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase(),
    ));
    window.dispatchEvent(new CustomEvent('ft:encounter-rows-patched', {
        detail: { encounterId, encounterArr, creatureIds: [...creatureIds], rows: patchedRows },
    }));
    window.dispatchEvent(new CustomEvent('ft:encounter-state-received', {
        detail: {
            encounterId,
            encounterArr,
            subType: 'creature_rows_patched',
            changedCreatureIds: [...creatureIds],
            inventoryUnchanged: true,
        },
    }));
    // An open side-info card showing something the patched creatures carry (their pack, a status
    // instance) repaints; the drawer's own reach gate for the viewer's row is nudged by the delta handler.
    try {
        if (typeof window.ftRerenderOpenSideInfo === 'function' && typeof window.ftCollectInventoryIds === 'function') {
            const ids: string[] = [];
            for (const row of patchedRows) ids.push(...window.ftCollectInventoryIds(row));
            if (ids.length) window.ftRerenderOpenSideInfo(ids);
        }
    } catch { /* non-fatal */ }
    ftUpdateActivityTokenBars();
    ftDebugLog('ft3d encounter', { rowsPatched: encounterId, creatures: creatureIds.length });
}

window.ftRealiasCastersFromEncounterBlob = ftRealiasCastersFromEncounterBlob;
window.handleEncounterUpdate = handleEncounterUpdate;

export {
    encounterContainsCaster,
    viewerPlayerCasterId,
    handleEncounterUpdate,
    handleEncounterRowsPatched,
    encounterSpatialBoundsChanged,
    ftRealiasCastersFromEncounterBlob,
    statusRowsOf,
};
